import { Injectable } from '@angular/core';

import { DEFAULT_PRICE_CURRENCY, PriceCurrency, formatMoney, parsePriceCurrency } from 'src/app/core/price-currency';
import { log } from 'src/app/core/log';
import { ApisService } from './apis.service';

/**
 * La moneda del comercio: la de su país (`venue.country` -> `country.priceCurrency`), con el
 * respaldo `$` (plan `mitter-packages/docs/plans/2026-11-01-vitrina-ropa-fotos-moneda.md`).
 *
 * La usan las pantallas del catálogo, que son lo que ve el cliente en la vitrina: Productos, el
 * editor, la vista previa y la muestra de «Apariencia». **La comanda y las analíticas siguen con
 * la del ambiente** (`UtilService.getCurrecySymbol()`): el delivery es solo de Venezuela.
 *
 * Se lee una vez por sesión del comercio (por `uid`): el admin la cambia muy de vez en cuando, y
 * una recarga de la página la vuelve a leer. Mientras tanto, o si no se pudo leer, dólares.
 */
@Injectable({ providedIn: 'root' })
export class StoreCurrencyService {
  /** La que se pinta ahora: dólares hasta que termine `load()`. */
  current: PriceCurrency = { ...DEFAULT_PRICE_CURRENCY };

  private loading: Promise<PriceCurrency> | null = null;
  private loadedFor: string | null = null;

  constructor(private api: ApisService) {}

  get symbol(): string {
    return this.current.symbol;
  }

  /** `$89,90`, `S/ 89,90`: el formato de la vitrina (ver `formatMoney`). */
  format(amount: number): string {
    return formatMoney(amount, this.current.symbol);
  }

  /**
   * Lee la moneda del comercio con sesión, una sola vez. Nunca rechaza: si no se puede leer
   * quedan dólares, y la próxima llamada vuelve a intentar.
   */
  load(): Promise<PriceCurrency> {
    const uid = localStorage.getItem('uid');
    if (this.loading !== null && this.loadedFor === uid) {
      return this.loading;
    }
    if (this.loadedFor !== uid) {
      //  Otro comercio en la misma pestaña: la del anterior no vale para este.
      this.current = { ...DEFAULT_PRICE_CURRENCY };
    }
    this.loadedFor = uid;
    const loading = this.read(uid).then(
      (currency) => {
        if (this.loading === loading) {
          this.current = currency;
        }
        return currency;
      },
      (error: unknown) => {
        log('no se pudo leer la moneda del comercio', error);
        if (this.loading === loading) {
          this.loading = null;
          this.current = { ...DEFAULT_PRICE_CURRENCY };
        }
        return { ...DEFAULT_PRICE_CURRENCY };
      }
    );
    this.loading = loading;
    return loading;
  }

  private async read(uid: string | null): Promise<PriceCurrency> {
    if (!uid) {
      return { ...DEFAULT_PRICE_CURRENCY };
    }
    const venue = await this.api.getVenueDetails(uid);
    return parsePriceCurrency(await this.api.getCountryPriceCurrency(venue?.country));
  }
}
